/**
 * Status banner for the /process Review step when 2+ documents were uploaded.
 * Variants (spec 2026-06-10-process-multidoc-integration-design.md):
 *  - fallback: merge failed, page silently used the first document — make it visible
 *  - correlation: documents may not belong together (warn, never block)
 *  - success: merged N documents, X conflicts auto-resolved
 */
import React, { useState } from 'react';
import { Alert, AlertTitle, Box } from '@mui/material';

interface MergeStatusBannerProps {
  documentsProcessed: number;
  autoResolvedConflicts: number;
  mergeFallback: boolean;
  correlationResult: any | null;
}

const MergeStatusBanner: React.FC<MergeStatusBannerProps> = ({
  documentsProcessed,
  autoResolvedConflicts,
  mergeFallback,
  correlationResult,
}) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  if (mergeFallback) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="warning" onClose={() => setDismissed(true)}>
          <AlertTitle>Merge failed — using first document only</AlertTitle>
          The other uploaded documents were not merged. Review the form values carefully.
        </Alert>
      </Box>
    );
  }

  if (documentsProcessed < 2) return null;

  const status = correlationResult?.correlation_status ?? correlationResult?.overall_status;
  if (status === 'not_correlated' || status === 'partially_correlated') {
    const reasons: string[] = (correlationResult?.uncorrelated_documents ?? [])
      .flatMap((d: any) => d?.reasons ?? [])
      .filter(Boolean);
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="warning" onClose={() => setDismissed(true)}>
          <AlertTitle>These documents may not belong to the same shipment</AlertTitle>
          {reasons.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : (
            'Cross-document correlation checks did not fully match.'
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity="info" onClose={() => setDismissed(true)}>
        {`Merged ${documentsProcessed} documents — ${autoResolvedConflicts} conflicts auto-resolved`}
      </Alert>
    </Box>
  );
};

export default MergeStatusBanner;
